import {
  getMySkillOverview,
  getMySkills,
  getUsersPointsProgress,
  getQuickQuestion,
  listConnections,
  listDepartments,
  listSkillTracks,
  listSkills,
  getSkillWithVerifier,
  getSkillVerifiers,
  tagsByOrganizationGroup,
  getLeaderboards,
} from "@/graphql/queries";
import { useQuery } from "@tanstack/react-query";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLQuery } from "@aws-amplify/api";
import {
  addUserGuidePoint,
  assignSkills,
  deleteSkillUser,
  recommendToUser,
  verifySkill,
} from "@/graphql/mutations";
import { sortRecordsByKey } from "@/utils/sortGraphqlRecords";
import { customGetLeaderboards } from "@/graphql/customQueries/getLeaderboard";

interface Skill {
  organizationGroup: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  _version: number;
  _deleted: null | any; // You can specify the actual type here if needed
  _lastChangedAt: number;
  departmentSkillsId: string;
  departmentSkillTagId: string;
  __typename: string;
}

export interface Department {
  id: string;
  name: string;
  description: null | string;
  organizationGroup: string;
  skills: {
    items: Skill[];
    nextToken: null | any; // You can specify the actual type here if needed
    startedAt: null | any; // You can specify the actual type here if needed
    __typename: string;
  };
  skillTracks: {
    items: any[]; // You can specify the actual type here if needed
    nextToken: null | any; // You can specify the actual type here if needed
    startedAt: null | any; // You can specify the actual type here if needed
    __typename: string;
  };
  createdAt: string;
  updatedAt: string;
  _version: number;
  _deleted: null | any; // You can specify the actual type here if needed
  _lastChangedAt: number;
  __typename: string;
}

export const fetchListOfDepartments = async () => {
  const response: any = await API.graphql(
    graphqlOperation(listDepartments, {
      filter: {
        _deleted: {
          ne: true,
        },
      },
    }),
  );
  const records: Department[] = response?.data?.listDepartments?.items ?? [];

  const filteredUserGuide = records?.filter((x) => x?.name !== "User Guide");

  return sortRecordsByKey(filteredUserGuide, "name");
};

export const fetchListOfExpertise = async (orgGroup: string) => {
  const response: any = await API.graphql(
    graphqlOperation(tagsByOrganizationGroup, {
      organizationGroup: orgGroup,
      filter: { _deleted: { ne: true } },
    }),
  );
  const records: any[] = response?.data?.tagsByOrganizationGroup?.items ?? [];

  return records;
};

export const fetchListOfSkillTracks = async (departmentId?: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listSkillTracks, {
      filter: {
        departmentId: {
          eq: departmentId,
        },
      },
    }),
  );
  const records: any[] = response?.data?.listSkillTracks?.items ?? [];

  return sortRecordsByKey(records, "name");
};

export const fetchListOfSkill = async (skillName?: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listSkills, {
      filter: {
        name: {
          contains: skillName,
        },
      },
    }),
  );
  const records: any[] = response?.data?.listSkills?.items ?? [];

  return sortRecordsByKey(records, "name");
};

export const useGetDepartments = (disabled?: boolean) => {
  return useQuery({
    queryKey: [`KEY_GET_DEPARTMENTS`],
    queryFn: () => fetchListOfDepartments(),
    enabled: !disabled
  });
};

export const useGetExpertise = (orgGroup: string) => {
  return useQuery({
    queryKey: [`KEY_GET_EKSPERTISE`, orgGroup],
    queryFn: () => fetchListOfExpertise(orgGroup),
  });
};

export const useGetSkillTracks = (departmentId?: string) => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_SKILL_TRACKS", departmentId], // Corrected queryKey
    queryFn: () => fetchListOfSkillTracks(departmentId),
  });
};

export const useGetSkill = (skillName: string) => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_SKILL", skillName], // Corrected queryKey
    queryFn: () => fetchListOfSkill(skillName),
  });
};

interface IAddSkillGoal {}

export const addSkillGoal = async (
  skillTrackId?: string,
  multipleSkill?: string[],
) => {
  return await API.graphql<GraphQLQuery<IAddSkillGoal>>({
    query: assignSkills,
    variables: {
      skillIds: multipleSkill,
      skillTrackId: skillTrackId,
    },
  });
};

async function fetchGetMySkills(userId?: string) {
  const response: any = await API.graphql(
    graphqlOperation(getMySkills, {
      userId,
    }),
  );
  const records: any[] = response?.data?.getMySkills ?? [];

  return sortRecordsByKey(records, "skillTrackName");
}

export const useGetMySkills = (userId?: string) => {
  return useQuery({
    queryKey: ["KEY_GET_MY_SKILL", userId],
    queryFn: () => fetchGetMySkills(userId),
  });
};

export interface ISkillOverviewRespond {
  allPossiblePoints: number;
  allSkillCount: number;
  completedPoints: number;
  completedPointsIncrementPercentage: number;
  completedSkillCount: number;
  completedSkillIncrementPercentage: number;
  allGoalsTarget: number;
  allGoalsCompleted: number;
  allGoalsIncrementPercentage: number;
}

async function fetchGetMySkillOverview(id?: string) {
  const response: any = await API.graphql(
    graphqlOperation(getMySkillOverview, {
      userId: id,
    }),
  );
  const records: ISkillOverviewRespond =
    response?.data?.getMySkillOverview ?? {};

  return records;
}

export const useGetMySkillOverview = (id?: string) => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_MY_SKILL_OVERVIEW"], // Corrected queryKey
    queryFn: () => fetchGetMySkillOverview(id),
  });
};

export const useGetPointProgress = (id?: string) => {
  return useQuery({
    queryKey: ["KEY_GET_POINT_PROGRESS", id || ""], // Corrected queryKey
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(getUsersPointsProgress, {
          userId: id,
        }),
      );
      const records: any = response?.data?.getUsersPointsProgress ?? {};

      return records;
    },
  });
};

export interface IQuickQuest {
  field: string;
  label: string;
  options: null | string[];
  type:
    | "string_array"
    | "text"
    | "boolean"
    | "number"
    | "options"
    | "date"
    | "departments"
    | "expertises";
  max: number;
  parent: string;
  isSecondTime?: boolean | null;
  category?: string;
  labelIsHTML?: boolean | null;
}

async function fetchGetQuickQuestion() {
  const response: any = await API.graphql(graphqlOperation(getQuickQuestion));
  const records: IQuickQuest = response?.data?.getQuickQuestion ?? {};

  return records;
}

export const useGetQuickQuestion = () => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_QUICK_QUESTION"], // Corrected queryKey
    queryFn: () => fetchGetQuickQuestion(),
  });
};

type IStatusConnection = "APPROVED" | "PENDING";

export const fetchMyConnection = async (
  id: string,
  userTargetType: string,
  status?: IStatusConnection,
) => {
  const response: any = await API.graphql(
    graphqlOperation(listConnections, {
      filter: {
        or: [
          {
            connectionUserToId: { eq: id }, //
            userFromType: { eq: userTargetType },
          },
          {
            connectionUserFromId: { eq: id },
            userToType: { eq: userTargetType },
          },
        ],
        status: { eq: status ? status : "APPROVED" },
        _deleted: { ne: true },
      },
      limit: 1000,
    }),
  );

  const records: any = response?.data?.listConnections ?? {};

  const result: any = [];
  records?.items.map((row: any) => {
    if (row?.userTo?.id === id) {
      result.push(row?.userFrom);
    } else {
      result.push(row?.userTo);
    }
  });

  return sortRecordsByKey(result, "userFrom.firstName");
};

export const useGetMyConnections = (
  id: string,
  userTargetType: string,
  status?: IStatusConnection,
) => {
  return useQuery({
    queryKey: [`KEY_GET_MY_CONNECTIONS_${userTargetType}`, id, status], // Corrected queryKey
    queryFn: () => fetchMyConnection(id, userTargetType, status),
  });
};

export const useGetListSkillTracks = () => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_LIST_SKILL_TRACKS"], // Corrected queryKey
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(listSkillTracks),
      );
      const records: any[] = response?.data?.listSkillTracks?.items ?? [];

      return sortRecordsByKey(records, "name");
    },
  });
};

export const useGetListSkills = () => {
  return useQuery({
    // @ts-ignore
    queryKey: ["KEY_GET_LIST_SKILLS"], // Corrected queryKey
    queryFn: async () => {
      const response: any = await API.graphql(graphqlOperation(listSkills));
      const records: any[] = response?.data?.listSkills?.items ?? [];

      return sortRecordsByKey(records, "skillTrack.name");
    },
  });
};

export const setRecommendSkill = async (
  id: string | string[],
  targetUser: string,
  type: "SKILL_TRACK" | "SKILL",
  isFromHR: boolean,
  verifierID?: string,
) => {
  return await API.graphql<GraphQLQuery<{}>>({
    query: recommendToUser,
    variables: {
      purpose: "CREATE",
      type: type,
      typeID: id,
      targetUser,
      isFromHR: isFromHR,
      verifierID,
    },
  });
};

interface IsetRequestVerifySkill {
  purpose: "CREATE" | "ACCEPTED" | "DECLINED" | "DELETE";
  targetObjectID: string;
  targetUserID?: string;
  rating?: number;
}

export const setRequestVerifySkill = async ({
  purpose,
  targetObjectID,
  targetUserID,
  rating,
}: IsetRequestVerifySkill) => {
  return await API.graphql<GraphQLQuery<{}>>({
    query: verifySkill,
    variables: {
      purpose,
      targetObjectID,
      targetUserID,
      rating,
    },
  });
};

export const useGetSkillVerifiers = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_SKILL_VERIFIER"],
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(getSkillWithVerifier, {
          targetUserId: id,
        }),
      );
      const records: any[] = response?.data?.getSkillWithVerifier ?? [];

      return sortRecordsByKey(records, "verifier.user.firstName");
    },
    enabled: false,
  });
};

export const useGetSkillNotWithVerifiers = (skillId?: string) => {
  return useQuery({
    queryKey: ["KEY_GET_SKILL_NOT_WITH_VERIFIER"],
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(getSkillVerifiers, {
          skillId,
        }),
      );
      const records: any[] = response?.data?.getSkillVerifiers ?? [];

      return sortRecordsByKey(records, "user.firstName");
    },
    enabled: false,
  });
};

interface Leaderboard {
  firstName: string;
  id: string;
  lastName: string;
  lifetimePoints: number;
}

export const useGetLeaderboard = (isMyDepartment: boolean) => {
  return useQuery({
    queryKey: ["KEY_GET_LEADERBOARD", isMyDepartment],
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(customGetLeaderboards, {
          isMyDepartment,
        }),
      );
      const records: Leaderboard[] = response?.data?.getLeaderboards ?? [];
      return records;
    },
  });
};

export const useGetNewLeaderboard = (isMyDepartment: boolean) => {
  return useQuery({
    queryKey: ["KEY_GET_NEW_LEADERBOARD", isMyDepartment],
    queryFn: async () => {
      const response: any = await API.graphql(
        graphqlOperation(getLeaderboards, {
          isMyDepartment,
        }),
      );
      const records: Leaderboard[] = response?.data?.getLeaderboards ?? [];
      return records;
    },
  });
};

export const deleteSkillFromUser = async (id: string, _version: any) => {
  return await API.graphql<GraphQLQuery<{}>>({
    query: deleteSkillUser,
    variables: {
      input: {
        id,
        _version,
      },
    },
  });
};

export const addUserGuidePoinMutation = async (skillId: string) => {
  return await API.graphql<GraphQLQuery<{}>>({
    query: addUserGuidePoint,
    variables: {
      skillId,
    },
  });
};
